import { type FC, type ReactNode } from 'react';

import { BrandBackdrop } from './brand-backdrop';

interface EmptyStateProps {
  icon?: ReactNode;
  /** Omitted for the no-access cases, which stay plain by decision. */
  title?: string;
  description: string;
  action?: ReactNode;
  /**
   * Announce the panel when it appears. Only true where the panel replaces the
   * grid in answer to something the user just typed; on first load it is the
   * page's own content and announcing it is noise.
   */
  announce?: boolean;
  /** Both designed cases carry it. The undesigned no-access cases stay plain. */
  backdrop?: boolean;
}

/**
 * The redesign's empty state. Shared on the terms the project list set when it
 * wrote this: local deliberately, extract when a consumer that survives the flag
 * turns up. The Agents tab is that consumer.
 *
 * Shares a name with the page-local empty states in meeting-secretary/ and
 * report-generation/, which are unrelated components on a different design. All
 * are imported by path, so nothing is ambiguous.
 *
 * Panel height is padding-driven, not fixed: 80px top and bottom around a 480px
 * content column. Measured in the browser the with-action case is 330, matching
 * its frame exactly. The search case is 270 against a frame of 262, because the
 * two frames disagree about the icon-to-text gap, 24 in one and 16 in the other.
 * 24 for both is the agreed value; the search frame has not been updated to match.
 *
 * text-secondary is `raw-stone-600` and not the token of that name. The frame's
 * text-secondary is #57534E; the exported --ds-color-text-secondary is #78716C,
 * which is what Figma calls "muted foreground". Drift for the token re-export to
 * settle, not something to paper over here.
 */
export const EmptyState: FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  announce,
  backdrop,
}) => (
  // overflow-hidden clips the canvas to the 16px radius; relative is the surface
  // the backdrop positions against.
  <div
    className="relative overflow-hidden rounded-[var(--ds-radius-16)] bg-[var(--ds-color-bg-secondary)]"
    role={announce ? 'status' : undefined}
  >
    {backdrop && <BrandBackdrop shape="edges" />}
    {/* The canvas is absolutely positioned and the copy is not, so without a
        stacking context of its own the copy paints underneath it. Padding sits
        here rather than on the surface, which leaves the geometry unchanged. */}
    <div className="relative flex flex-col items-center gap-6 px-8 py-20 text-center">
      {icon && <span className="text-[var(--ds-color-bg-icon-primary)]">{icon}</span>}
      <div className="flex max-w-[480px] flex-col gap-2">
        {title && (
          // h2 rather than a styled p: on both pages the h1 is above this.
          <h2 className="text-[18px] leading-[22px] font-medium text-[var(--ds-color-text-primary)]">
            {title}
          </h2>
        )}
        {/* pre-line so a translator can put the break where their sentence wants it. */}
        <p className="text-[12px] leading-[16px] whitespace-pre-line text-[var(--ds-color-raw-stone-600)]">
          {description}
        </p>
      </div>
      {action}
    </div>
  </div>
);
